import { useMemo } from 'react';
import { calculateGuideBounds } from '../utils/faceGuidanceLogic';
import { HeadTurnArrowCue } from './HeadTurnArrowCue';
import './FaceGuideOverlay.css';

const CAPTURE_STEPS = [
  { id: 'front', label: 'Front' },
  { id: 'left', label: 'Left' },
  { id: 'right', label: 'Right' },
];

const MESSAGE_HINTS = {
  'Move face left': { icon: '←', label: 'Shift left' },
  'Move face right': { icon: '→', label: 'Shift right' },
  'Move closer': { icon: '↔', label: 'Move closer' },
  'Move a little farther': { icon: '↔', label: 'Step back' },
  'Tilt head right': { icon: '↻', label: 'Level head' },
  'Tilt head left': { icon: '↺', label: 'Level head' },
  'Lower your head': { icon: '↓', label: 'Lower chin' },
  'Raise your head': { icon: '↑', label: 'Raise chin' },
  'Turn your head to the right': { icon: '↷', label: 'Turn right' },
  'Turn your head to the left': { icon: '↶', label: 'Turn left' },
  'Perfect alignment!': { icon: '✓', label: 'Hold still' },
  'Perfect! Hold still.': { icon: '✓', label: 'Hold still' },
};

function getDeviceType() {
  if (typeof window === 'undefined') return 'desktop';
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  if (width < 1440) return 'small-laptop';
  return 'desktop';
}

function getCornerIndicatorLength() {
  switch (getDeviceType()) {
    case 'mobile': return 15;
    case 'tablet': return 18;
    default: return 20;
  }
}

function getInnerGlowOffset() {
  switch (getDeviceType()) {
    case 'mobile': return 4;
    case 'tablet': return 5;
    default: return 6;
  }
}

function stepState(stepId, captureStep) {
  const order = ['front', 'left', 'right'];
  const currentIdx = order.indexOf(captureStep);
  const stepIdx = order.indexOf(stepId);
  if (stepIdx < 0 || currentIdx < 0) return 'pending';
  if (stepIdx < currentIdx) return 'done';
  if (stepIdx === currentIdx) return 'active';
  return 'pending';
}

const FaceGuideOverlay = ({
  statusColor = 'red',
  message = 'Position your face',
  videoWidth = 640,
  videoHeight = 480,
  showOverlay = true,
  countdown = null,
  lightingOk = false,
  faceDetected = false,
  faceStraight = false,
  voiceDisabled = false,
  headTurnDirection = null,
  captureStep = 'front',
  alignmentScore = 0,
  maxScore = 5,
  showStepProgress = true,
}) => {
  const guideDimensions = useMemo(() => {
    const bounds = calculateGuideBounds(videoWidth, videoHeight);
    return {
      centerX: bounds.centerX,
      centerY: bounds.centerY,
      width: bounds.width,
      height: bounds.height,
      topPercent: (bounds.centerY - bounds.height / 2) / videoHeight,
      leftPercent: (bounds.centerX - bounds.width / 2) / videoWidth,
    };
  }, [videoWidth, videoHeight]);

  const cornerLength = useMemo(() => getCornerIndicatorLength(), []);
  const innerGlowOffset = useMemo(() => getInnerGlowOffset(), []);
  const effectiveScore = alignmentScore || (
    statusColor === 'green' ? maxScore : statusColor === 'yellow' ? Math.ceil(maxScore * 0.6) : Math.max(1, Math.ceil(maxScore * 0.2))
  );
  const progressPct = maxScore > 0 ? Math.round((effectiveScore / maxScore) * 100) : 0;
  const hint = MESSAGE_HINTS[message] || null;

  if (!showOverlay) return null;

  const colorMap = {
    green: { stroke: '#4ade80', fill: 'rgba(74, 222, 128, 0.12)', text: '#22c55e' },
    yellow: { stroke: '#f59e0b', fill: 'rgba(245, 158, 11, 0.16)', text: '#92400e' },
    red: { stroke: '#ef4444', fill: 'rgba(239, 68, 68, 0.1)', text: '#dc2626' },
    orange: { stroke: '#f97316', fill: 'rgba(249, 115, 22, 0.12)', text: '#ea580c' },
  };
  const colors = colorMap[statusColor] || colorMap.red;

  return (
    <div className={`face-guide-overlay face-guide-overlay--${statusColor}`}>
      {showStepProgress && captureStep !== 'done' ? (
        <div className="face-guide-step-track" aria-label="Capture progress">
          {CAPTURE_STEPS.map((step) => {
            const state = stepState(step.id, captureStep);
            return (
              <div key={step.id} className={`face-guide-step-pill face-guide-step-pill--${state}`}>
                <span className="face-guide-step-pill__dot" aria-hidden="true">
                  {state === 'done' ? '✓' : state === 'active' ? '●' : '○'}
                </span>
                <span>{step.label}</span>
              </div>
            );
          })}
        </div>
      ) : null}

      <svg
        className="face-guide-svg"
        width="100%"
        height="100%"
        viewBox={`0 0 ${videoWidth} ${videoHeight}`}
        preserveAspectRatio="none"
      >
        <rect
          className="face-guide-rect"
          x={guideDimensions.centerX - guideDimensions.width / 2}
          y={guideDimensions.centerY - guideDimensions.height / 2}
          width={guideDimensions.width}
          height={guideDimensions.height}
          rx={guideDimensions.width * 0.12}
          ry={guideDimensions.width * 0.12}
          fill={colors.fill}
          stroke={colors.stroke}
          strokeWidth="4"
          strokeDasharray={statusColor === 'green' ? '0' : '12,6'}
          opacity={0.92}
        />

        {statusColor === 'green' ? (
          <rect
            className="face-guide-inner-rect face-guide-inner-rect--pulse"
            x={guideDimensions.centerX - guideDimensions.width / 2 + innerGlowOffset}
            y={guideDimensions.centerY - guideDimensions.height / 2 + innerGlowOffset}
            width={guideDimensions.width - (innerGlowOffset * 2)}
            height={guideDimensions.height - (innerGlowOffset * 2)}
            rx={guideDimensions.width * 0.12}
            ry={guideDimensions.width * 0.12}
            fill="none"
            stroke={colors.stroke}
            strokeWidth="2"
            opacity={0.55}
          />
        ) : null}

        {statusColor !== 'green' ? (
          <>
            <line className="face-guide-corner-line" x1={guideDimensions.centerX - guideDimensions.width / 2} y1={guideDimensions.centerY - guideDimensions.height / 2 + cornerLength} x2={guideDimensions.centerX - guideDimensions.width / 2} y2={guideDimensions.centerY - guideDimensions.height / 2} stroke={colors.stroke} strokeWidth="3" opacity={0.7} />
            <line className="face-guide-corner-line" x1={guideDimensions.centerX - guideDimensions.width / 2 + cornerLength} y1={guideDimensions.centerY - guideDimensions.height / 2} x2={guideDimensions.centerX - guideDimensions.width / 2} y2={guideDimensions.centerY - guideDimensions.height / 2} stroke={colors.stroke} strokeWidth="3" opacity={0.7} />
            <line className="face-guide-corner-line" x1={guideDimensions.centerX + guideDimensions.width / 2} y1={guideDimensions.centerY - guideDimensions.height / 2 + cornerLength} x2={guideDimensions.centerX + guideDimensions.width / 2} y2={guideDimensions.centerY - guideDimensions.height / 2} stroke={colors.stroke} strokeWidth="3" opacity={0.7} />
            <line className="face-guide-corner-line" x1={guideDimensions.centerX + guideDimensions.width / 2 - cornerLength} y1={guideDimensions.centerY - guideDimensions.height / 2} x2={guideDimensions.centerX + guideDimensions.width / 2} y2={guideDimensions.centerY - guideDimensions.height / 2} stroke={colors.stroke} strokeWidth="3" opacity={0.7} />
            <line className="face-guide-corner-line" x1={guideDimensions.centerX - guideDimensions.width / 2} y1={guideDimensions.centerY + guideDimensions.height / 2 - cornerLength} x2={guideDimensions.centerX - guideDimensions.width / 2} y2={guideDimensions.centerY + guideDimensions.height / 2} stroke={colors.stroke} strokeWidth="3" opacity={0.7} />
            <line className="face-guide-corner-line" x1={guideDimensions.centerX - guideDimensions.width / 2 + cornerLength} y1={guideDimensions.centerY + guideDimensions.height / 2} x2={guideDimensions.centerX - guideDimensions.width / 2} y2={guideDimensions.centerY + guideDimensions.height / 2} stroke={colors.stroke} strokeWidth="3" opacity={0.7} />
            <line className="face-guide-corner-line" x1={guideDimensions.centerX + guideDimensions.width / 2} y1={guideDimensions.centerY + guideDimensions.height / 2 - cornerLength} x2={guideDimensions.centerX + guideDimensions.width / 2} y2={guideDimensions.centerY + guideDimensions.height / 2} stroke={colors.stroke} strokeWidth="3" opacity={0.7} />
            <line className="face-guide-corner-line" x1={guideDimensions.centerX + guideDimensions.width / 2 - cornerLength} y1={guideDimensions.centerY + guideDimensions.height / 2} x2={guideDimensions.centerX + guideDimensions.width / 2} y2={guideDimensions.centerY + guideDimensions.height / 2} stroke={colors.stroke} strokeWidth="3" opacity={0.7} />
            <line
              className="face-guide-scan-line"
              x1={guideDimensions.centerX - guideDimensions.width / 2}
              x2={guideDimensions.centerX + guideDimensions.width / 2}
              y1={guideDimensions.centerY - guideDimensions.height / 2}
              y2={guideDimensions.centerY - guideDimensions.height / 2}
              stroke={colors.stroke}
              strokeWidth="2"
              opacity="0.45"
            />
          </>
        ) : null}

        {(headTurnDirection === 'left' || headTurnDirection === 'right') && (
          <g className={`face-guide-head-turn-edge face-guide-head-turn-edge--${headTurnDirection}`} stroke={colors.stroke} fill="none" strokeWidth={Math.max(3, videoWidth * 0.004)} strokeLinecap="round" strokeLinejoin="round" opacity={0.88}>
            {headTurnDirection === 'right' ? (
              <g transform={`translate(${guideDimensions.centerX + guideDimensions.width / 2 + videoWidth * 0.02}, ${guideDimensions.centerY})`}>
                <g className="face-guide-head-turn-edge__shift">
                  <polyline points="-6,-22 10,0 -6,22" />
                  <polyline points="10,-22 26,0 10,22" />
                  <polyline points="26,-22 42,0 26,22" />
                </g>
              </g>
            ) : (
              <g transform={`translate(${guideDimensions.centerX - guideDimensions.width / 2 - videoWidth * 0.02}, ${guideDimensions.centerY})`}>
                <g className="face-guide-head-turn-edge__shift">
                  <polyline points="6,-22 -10,0 6,22" />
                  <polyline points="-10,-22 -26,0 -10,22" />
                  <polyline points="-26,-22 -42,0 -26,22" />
                </g>
              </g>
            )}
          </g>
        )}
      </svg>

      <div className="face-guide-hud">
        <div className="face-guide-alignment-card">
          <div className="face-guide-alignment-ring" style={{ '--fg-progress': `${progressPct}%`, '--fg-accent': colors.stroke }}>
            <span className="face-guide-alignment-value">{progressPct}%</span>
          </div>
          <div className="face-guide-alignment-copy">
            <span className="face-guide-alignment-label">Alignment</span>
            <span className="face-guide-alignment-score">{effectiveScore}/{maxScore}</span>
          </div>
        </div>

        <div className="status-indicators-box">
          <div className={`status-indicator ${lightingOk ? 'is-ok' : 'is-bad'}`}>
            <span className={`status-icon ${lightingOk ? 'status-ok' : 'status-bad'}`}>{lightingOk ? '✓' : '!'}</span>
            <span className="status-label">Lighting</span>
          </div>
          <div className={`status-indicator ${faceDetected ? 'is-ok' : 'is-bad'}`}>
            <span className={`status-icon ${faceDetected ? 'status-ok' : 'status-bad'}`}>{faceDetected ? '✓' : '○'}</span>
            <span className="status-label">Face</span>
          </div>
          <div className={`status-indicator ${faceStraight ? 'is-ok' : 'is-bad'}`}>
            <span className={`status-icon ${faceStraight ? 'status-ok' : 'status-bad'}`}>{faceStraight ? '✓' : '↕'}</span>
            <span className="status-label">Level</span>
          </div>
        </div>
      </div>

      {countdown !== null && countdown > 0 ? (
        <div
          className="countdown-timer"
          style={{
            top: `${(guideDimensions.centerY / videoHeight) * 100}%`,
            left: `${(guideDimensions.centerX / videoWidth) * 100}%`,
          }}
        >
          <span className="countdown-number">{countdown}</span>
        </div>
      ) : (
        <div
          className="guide-message-card"
          style={{
            top: `${((guideDimensions.centerY + guideDimensions.height * 0.34) / videoHeight) * 100}%`,
            left: `${(guideDimensions.centerX / videoWidth) * 100}%`,
            borderColor: colors.stroke,
          }}
        >
          {hint ? (
            <span className="guide-message-icon" style={{ color: colors.stroke }} aria-hidden="true">{hint.icon}</span>
          ) : null}
          <div className="guide-message-copy">
            <span className="guide-message-title" style={{ color: colors.text }}>{message}</span>
            {hint ? <span className="guide-message-sub">{hint.label}</span> : null}
          </div>
          {(headTurnDirection === 'left' || headTurnDirection === 'right') && (
            <HeadTurnArrowCue direction={headTurnDirection} accentColor={colors.stroke} />
          )}
          {voiceDisabled ? <span className="guide-message-hint">Follow the on-screen guide</span> : null}
        </div>
      )}
    </div>
  );
};

export default FaceGuideOverlay;
